/**
 * Sprint 8 — Hero device mockups (tablet POS + phone owner view).
 *
 * Pure CSS/SVG — no external images, no app iframe, no real workspace data.
 * All numbers below are sanitized demo values.
 */
import { ShoppingCart, Smartphone, Wifi, WifiOff } from "lucide-react";

/** Tablet POS mockup — rendered as a stylized counter screen. */
export function TabletPosMockup() {
  const cart = [
    { name: "Americano", qty: 2, price: 90 },
    { name: "Pandesal", qty: 6, price: 10 },
    { name: "Vanilla Latte", qty: 1, price: 130 },
  ];
  const subtotal = cart.reduce((s, r) => s + r.qty * r.price, 0);
  const total = subtotal;
  return (
    <div className="relative" data-testid="hero-tablet-mockup">
      {/* Tablet frame */}
      <div className="relative mx-auto w-full max-w-[520px] aspect-[4/3] rounded-[26px] bg-neutral-900 shadow-2xl shadow-black/30 dark:shadow-black/60 ring-1 ring-black/10 dark:ring-white/5 p-2.5">
        {/* Screen */}
        <div className="relative h-full w-full rounded-[18px] bg-background overflow-hidden flex">
          {/* Left — product grid */}
          <div className="flex-1 p-3 border-r border-border bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Menu</div>
              <div className="text-[9px] font-mono text-muted-foreground">bizone pos</div>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              {["Americano", "Latte", "Cappuccino", "Pandesal", "Croissant", "Donut", "Iced Tea", "Water", "Juice"].map((p, i) => (
                <div key={p} className="aspect-square rounded-md bg-background border border-border p-1.5 flex flex-col justify-between hover:bg-brand/5">
                  <div className={`h-6 w-6 rounded ${i % 3 === 0 ? "bg-amber-500/20" : i % 3 === 1 ? "bg-emerald-500/20" : "bg-blue-500/20"}`} />
                  <div className="text-[8px] leading-tight font-medium truncate">{p}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right — cart */}
          <div className="w-[42%] p-3 flex flex-col">
            <div className="flex items-center gap-1.5 mb-2">
              <ShoppingCart className="h-3 w-3 text-brand" />
              <div className="text-[10px] uppercase tracking-widest">Cart · 3 items</div>
            </div>
            <div className="flex-1 space-y-1 overflow-hidden">
              {cart.map((r) => (
                <div key={r.name} className="flex items-center justify-between text-[10px]">
                  <div className="truncate">{r.name} <span className="text-muted-foreground">× {r.qty}</span></div>
                  <div className="font-mono tabular-nums">₱{(r.qty * r.price).toLocaleString()}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 pt-2 border-t border-border space-y-1">
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Subtotal</span><span className="font-mono tabular-nums">₱{subtotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold">
                <span>Total</span><span className="font-mono tabular-nums">₱{total.toLocaleString()}</span>
              </div>
              <div className="mt-1.5 grid grid-cols-4 gap-1">
                {["Cash", "GCash", "Card", "Utang"].map((m) => (
                  <div key={m} className={`text-[8px] rounded px-1 py-1 text-center font-medium ${m === "Cash" ? "bg-brand text-brand-foreground" : "bg-muted"}`}>{m}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Offline chip */}
      <div className="absolute -bottom-3 left-6 flex items-center gap-1.5 rounded-full border border-border bg-background px-2.5 py-1 shadow-md text-[10px]">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
        Offline-ready
      </div>
    </div>
  );
}

/** Phone owner-view mockup — stacked KPI dashboard. */
export function PhoneOwnerMockup() {
  return (
    <div className="relative" data-testid="hero-phone-mockup">
      <div className="relative mx-auto w-[180px] sm:w-[200px] aspect-[9/19] rounded-[32px] bg-neutral-900 shadow-2xl shadow-black/30 dark:shadow-black/60 ring-1 ring-black/10 dark:ring-white/5 p-1.5 rotate-3">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 h-3.5 w-16 rounded-full bg-neutral-900 z-10" />
        {/* Screen */}
        <div className="relative h-full w-full rounded-[26px] bg-background overflow-hidden">
          <div className="h-5 bg-muted/50 flex items-center justify-between px-4 text-[8px] font-mono text-muted-foreground">
            <span>9:41</span>
            <span className="flex items-center gap-0.5"><Wifi className="h-2 w-2" /></span>
          </div>
          <div className="p-2.5 space-y-2">
            <div>
              <div className="text-[8px] uppercase tracking-widest text-muted-foreground">Owner view</div>
              <div className="text-[10px] font-semibold mt-0.5">Juan's Sari-Sari</div>
            </div>
            <div className="rounded-lg border border-border p-2 bg-gradient-to-br from-brand/10 to-transparent">
              <div className="text-[8px] uppercase tracking-widest text-muted-foreground">Today's Sales</div>
              <div className="font-display text-base tabular-nums mt-0.5">₱12,450</div>
              <div className="text-[8px] text-emerald-600 dark:text-emerald-400 mt-0.5">↑ 42 transactions</div>
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <div className="rounded-lg border border-border p-1.5">
                <div className="text-[7px] uppercase tracking-wider text-muted-foreground">Expenses</div>
                <div className="text-[11px] font-semibold tabular-nums mt-0.5">₱1,850</div>
              </div>
              <div className="rounded-lg border border-border p-1.5">
                <div className="text-[7px] uppercase tracking-wider text-muted-foreground">Utang</div>
                <div className="text-[11px] font-semibold tabular-nums mt-0.5">₱4,200</div>
              </div>
              <div className="rounded-lg border border-border p-1.5 col-span-2 flex items-center justify-between">
                <div>
                  <div className="text-[7px] uppercase tracking-wider text-muted-foreground">Low stock</div>
                  <div className="text-[11px] font-semibold mt-0.5">6 items</div>
                </div>
                <div className="h-6 w-6 rounded-full bg-amber-500/20 grid place-items-center">
                  <Smartphone className="h-3 w-3 text-amber-600" />
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-border p-1.5">
              <div className="text-[7px] uppercase tracking-wider text-muted-foreground">Sales · 7d</div>
              <div className="mt-1 flex items-end gap-0.5 h-6">
                {[55, 72, 48, 90, 65, 40, 82].map((v, i) => (
                  <div key={i} className="flex-1 rounded-sm bg-foreground/60" style={{ height: `${v}%` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
